import { useId, useRef, useState } from "react";
import { ACCEPTED_EXTENSIONS, MAX_CLIP_SEC, MIN_CLIP_SEC } from "../../convex/lib/game";
import { useClipActions } from "./backend";
import { Badge } from "./Badge";
import { Button } from "./Button";
import type { Auth, Clip, ToastTone } from "./contracts";
import { useMedia } from "./media";
import { Meter } from "./Meter";
import { cx, formatTime, errorText } from "./util";

export interface ClipSlotProps {
  auth: Auth;
  /** Clips this player currently owns. */
  clips: Clip[];
  /** The room's segment length — a clip shorter than this can't be dealt. */
  segmentLength: number;
  disabled?: boolean;
  onPickSegment: (clip: Clip) => void;
  onToast: (text: string, tone?: ToastTone) => void;
}

/**
 * Upload goes straight from the browser to R2 via a presigned PUT — the bytes
 * never touch Convex. Validation happens BEFORE the request, so a bad file
 * costs nothing and the rejection names the actual problem.
 */
export function ClipSlot({
  auth,
  clips,
  segmentLength,
  disabled = false,
  onPickSegment,
  onToast,
}: ClipSlotProps) {
  const media = useMedia();
  const actions = useClipActions();
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const [progress, setProgress] = useState<number | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  /** File names are a local nicety — the server has no reason to store them. */
  const [names, setNames] = useState<Record<string, string>>({});

  const busy = progress !== null;

  async function handleFile(file: File) {
    setReasons([]);
    const probe = await media.inspectFile(file);
    // The probe hands us an object URL for local preview. Nothing on this
    // screen renders it, so release it rather than leaking one per clip.
    URL.revokeObjectURL(probe.previewUrl);

    if (!probe.ok) {
      setReasons(probe.reasons);
      onToast(probe.reasons[0] ?? "That clip won't work.", "error");
      return;
    }
    if (probe.durationSec < segmentLength) {
      const why = `That clip is ${Math.round(probe.durationSec)}s — this room dubs ${segmentLength}s at a time.`;
      setReasons([why]);
      onToast(why, "error");
      return;
    }

    setProgress(0);
    try {
      const { clipId, uploadUrl } = await actions.request(auth, {
        mimeType: file.type,
        durationSec: probe.durationSec,
        sizeBytes: file.size,
      });
      await media.upload(file, uploadUrl, setProgress);
      await actions.confirm(auth, clipId);
      setNames((prev) => ({ ...prev, [clipId]: file.name }));
      onToast("Clip uploaded. Now pick the funny bit.", "success");
    } catch (err) {
      const message = errorText(err, "Upload failed.");
      setReasons([message]);
      onToast(message, "error");
    } finally {
      setProgress(null);
    }
  }

  return (
    <div className="bd-clip-slot">
      {clips.map((clip) => (
        <div key={clip.clipId} className="row row-highlight">
          <span className="t-link grow truncate">{names[clip.clipId] ?? "Your clip"}</span>
          <span className="t-micro muted">{formatTime(clip.durationSec)}</span>
          {clip.dealable ? (
            <Badge tone="ready">Ready</Badge>
          ) : clip.ready ? (
            <Badge tone="amber">Pick a segment</Badge>
          ) : (
            <Badge tone="carbon">Uploading</Badge>
          )}
          <Button
            variant="primary"
            beacon={clip.ready && !clip.dealable}
            onClick={() => onPickSegment(clip)}
            disabled={!clip.ready}
          >
            Segment
          </Button>
        </div>
      ))}

      {busy ? (
        <Meter
          value={progress ?? 0}
          label="Upload progress"
          caption={`Uploading… ${Math.round((progress ?? 0) * 100)}%`}
        />
      ) : (
        <div
          className={cx("bd-drop", dragOver && "is-over")}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) void handleFile(file);
          }}
        >
          <label className="t-label" htmlFor={inputId}>
            {clips.length === 0
              ? "Drop a clip, or tap to choose one"
              : "Add a spare — spares are what you donate"}
          </label>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            className="bd-sr-only"
            accept={ACCEPTED_EXTENSIONS.join(",")}
            disabled={disabled}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = "";
            }}
          />
          <Button
            variant="primary"
            disabled={disabled}
            beacon={clips.length === 0 && !disabled}
            onClick={() => inputRef.current?.click()}
          >
            Choose a clip
          </Button>
          <p className="t-micro muted">
            MP4, MOV or WebM. {MIN_CLIP_SEC}&ndash;{MAX_CLIP_SEC} seconds, at least{" "}
            {segmentLength}s. Must have sound.
          </p>
        </div>
      )}

      {reasons.length > 0 ? (
        <div className="inset bd-pad bd-shake" role="alert">
          <p className="t-label danger">That clip won&rsquo;t work</p>
          <ul className="t-body bd-reasons">
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
